import * as React from 'react';
import { ls } from '../../i18n/translations';
import {
  capitalize,
  fromParsedToMachineType,
  fromParsedToMachineVersion,
  isHTHCompany,
  parseMachineType,
  parseMachineVersion,
} from '../../helpers/utils';
import { Box, IconButton, TextField } from '@mui/material';
import MUIDataTable, { MUIDataTableColumnDef, MUIDataTableOptions } from 'mui-datatables';
import { defaultMUIDataTableOptions } from '../../helpers/datatableUtils';
import { MachineType, MachineVersion, Module } from '../../graphql/codegen/graphql-react';
import SteielAutocomplete from '../ui/SteielAutocomplete';
import SteielSelect from '../ui/SteielSelect';
import { Clear as ClearIcon } from '@mui/icons-material';
import { useMQ } from '../../hook/useMQ';

const options: MUIDataTableOptions = {
  ...defaultMUIDataTableOptions,
  filter: false,
  sort: false,
  download: false,
  search: false,
  pagination: false,
  viewColumns: false,
  fixedSelectColumn: false,
};

function getEffectiveMachineType(module: Partial<Module>): MachineType {
  if (isHTHCompany() && (!module.type || module.type === MachineType.Unknown)) {
    return MachineType.Cycleau;
  }
  return (module.type as MachineType) || MachineType.Unknown;
}

function getEffectiveInstrumentDisplay(module: Partial<Module>): string {
  return parseMachineType(getEffectiveMachineType(module));
}

/** Display labels for the version dropdown for a given instrument row label (same switch as version column). */
function getAllowedVersionLabelsForInstrument(instrumentDisplayType: string): string[] {
  const allowedValues: string[] = [];
  switch (instrumentDisplayType) {
    case parseMachineType(MachineType.Cycleau): {
      allowedValues.push(parseMachineVersion(MachineVersion.First));
      allowedValues.push(parseMachineVersion(MachineVersion.Pro));
      break;
    }
    case parseMachineType(MachineType.Mc14Evo): {
      allowedValues.push(parseMachineVersion(MachineVersion.Mc14evoPHRxCle12Tc));
      allowedValues.push(parseMachineVersion(MachineVersion.Mc14evoPHRxCacTc));
      allowedValues.push(parseMachineVersion(MachineVersion.Mc14evo_4CanaliTc));
      break;
    }
    case parseMachineType(MachineType.Mco14Evo): {
      allowedValues.push(parseMachineVersion(MachineVersion.Mco14_3SezCl2));
      allowedValues.push(parseMachineVersion(MachineVersion.Mco14_5SezCl2ClTClC));
      break;
    }
    case parseMachineType(MachineType.NovaNsp): {
      allowedValues.push(parseMachineVersion(MachineVersion.Nsp161CMin));
      allowedValues.push(parseMachineVersion(MachineVersion.Nsp161Ma));
      allowedValues.push(parseMachineVersion(MachineVersion.Nsp162PHRx));
      allowedValues.push(parseMachineVersion(MachineVersion.Nsp163ACle11_12));
      allowedValues.push(parseMachineVersion(MachineVersion.Nsp163BCle16));
      break;
    }
    case parseMachineType(MachineType.Ef315): {
      allowedValues.push(parseMachineVersion(MachineVersion.PHRxTc));
      allowedValues.push(parseMachineVersion(MachineVersion.PHClTc));
      allowedValues.push(parseMachineVersion(MachineVersion.PHCacTc));
      break;
    }
    case parseMachineType(MachineType.Evolution): {
      allowedValues.push(parseMachineVersion(MachineVersion.Ew2Pool));
      break;
    }
    case parseMachineType(MachineType.Ew2Pool): {
      // HTH supports only the pH/CAC/T° version
      if (!isHTHCompany()) {
        allowedValues.push(parseMachineVersion(MachineVersion.PHOrpTc));
      }
      allowedValues.push(parseMachineVersion(MachineVersion.PHCacTc));
      if (!isHTHCompany()) {
        allowedValues.push(parseMachineVersion(MachineVersion.PHCleTc));
      }
      break;
    }
    case parseMachineType(MachineType.Nova2Pool): {
      allowedValues.push(parseMachineVersion(MachineVersion.PHOrpTc));
      allowedValues.push(parseMachineVersion(MachineVersion.PHCacTc));
      allowedValues.push(parseMachineVersion(MachineVersion.PHCleTc));
      break;
    }
    case parseMachineType(MachineType.Nova2PoolP): {
      allowedValues.push(parseMachineVersion(MachineVersion.PHOrpTc));
      allowedValues.push(parseMachineVersion(MachineVersion.PHCacTc));
      allowedValues.push(parseMachineVersion(MachineVersion.PHCleTc));
      break;
    }
    case parseMachineType(MachineType.Ew2): {
      allowedValues.push(parseMachineVersion(MachineVersion.Ew2_2CanaliTc));
      break;
    }
    case parseMachineType(MachineType.Ew): {
      allowedValues.push(parseMachineVersion(MachineVersion.Ew_1CanaleTc));
      break;
    }
    default:
      allowedValues.push(parseMachineVersion(MachineVersion.Unknown));
      break;
  }
  return allowedValues;
}

function resolveModelVersionAfterInstrumentChange(
  prevModule: Partial<Module>,
  instrumentSelection: string,
): MachineVersion {
  const allowed = getAllowedVersionLabelsForInstrument(instrumentSelection);
  const currentRaw = prevModule.modelVersion;
  const currentLabel = currentRaw ? parseMachineVersion(currentRaw as MachineVersion) : '';
  if (currentLabel && allowed.includes(currentLabel)) {
    return Object.values(MachineVersion).includes(currentRaw as MachineVersion)
      ? (currentRaw as MachineVersion)
      : fromParsedToMachineVersion(currentLabel);
  }
  return fromParsedToMachineVersion(allowed[0]);
}

function resolveDefaultModelVersion(module: Partial<Module>): MachineVersion {
  const raw = module.modelVersion;
  if (raw && raw !== MachineVersion.Unknown && String(raw).trim() !== '') {
    return raw as MachineVersion;
  }
  return resolveModelVersionAfterInstrumentChange(module, getEffectiveInstrumentDisplay(module));
}

interface ModulesTableProps {
  modules: Partial<Module>[];
  macOptions: string[];
  setFieldValue: (field: string, value: any) => void;
}

const ModulesTable: React.FC<ModulesTableProps> = ({ modules, macOptions, setFieldValue }) => {
  const { isMobile } = useMQ();

  React.useEffect(() => {
    if (!isHTHCompany()) {
      return;
    }
    const needsSync = (modules || []).some((m) => !m.type || m.type === MachineType.Unknown);
    if (!needsSync) {
      return;
    }
    const newModules = (modules || []).map((m) => {
      if (!m.type || m.type === MachineType.Unknown) {
        const instrumentDisplay = parseMachineType(MachineType.Cycleau);
        return {
          ...m,
          type: MachineType.Cycleau,
          modelVersion: resolveModelVersionAfterInstrumentChange(m, instrumentDisplay),
        };
      }
      return m;
    });
    setFieldValue('modules', newModules);
  }, [modules, setFieldValue]);

  const tableModules = ([...(modules || [])].map((m, i) => {
    const effectiveModelVersion = resolveDefaultModelVersion(m);
    return {
      mac: m.mac || '',
      name: m.name || '',
      tool: m.tool || '',
      type: getEffectiveInstrumentDisplay(m),
      version: parseMachineVersion(effectiveModelVersion),
    };
  }) || []) as {
    mac: string;
    name: string;
    type: string;
    tool: string;
    version: string;
  }[];

  const columns: MUIDataTableColumnDef[] = [
    {
      name: 'mac',
      label: ls.macAddress.toUpperCase(),
      options: {
        // eslint-disable-next-line react/display-name
        customBodyRender: (value, tableMeta, updateValue) => {
          return (
            <Box minWidth={200}>
              <SteielAutocomplete
                options={macOptions}
                value={value}
                onChange={(v) => {
                  const newModules = [...modules];
                  newModules[tableMeta.rowIndex] = { ...newModules[tableMeta.rowIndex], mac: v };
                  setFieldValue('modules', newModules);
                }}
              />
            </Box>
          );
        },
      },
    },
    {
      name: 'name',
      label: ls.name.toUpperCase(),
      options: {
        // eslint-disable-next-line react/display-name
        customBodyRender: (value, tableMeta, updateValue) => {
          return (
            <Box minWidth={200}>
              <TextField
                placeholder={capitalize(ls.name)}
                fullWidth
                size='small'
                InputProps={{ style: { fontSize: '1.1rem' } }}
                value={value}
                onChange={(e) => {
                  const newModules = [...modules];
                  newModules[tableMeta.rowIndex] = {
                    ...newModules[tableMeta.rowIndex],
                    name: e.target.value,
                  };
                  setFieldValue('modules', newModules);
                }}
              />
            </Box>
          );
        },
      },
    },
    {
      name: 'type',
      label: ls.tool.toUpperCase(),
      options: {
        // eslint-disable-next-line react/display-name
        customBodyRender: (value, tableMeta, updateValue) => {
          return (
            <Box>
              <SteielSelect
                allowedValues={
                  isHTHCompany()
                    ? [
                        parseMachineType(MachineType.Cycleau),
                        parseMachineType(MachineType.Ew2Pool),
                      ]
                    : [
                        parseMachineType(MachineType.Cycleau),
                        parseMachineType(MachineType.Mc14Evo),
                        parseMachineType(MachineType.Mco14Evo),
                        parseMachineType(MachineType.NovaNsp),
                        parseMachineType(MachineType.Ef315),
                        // parseMachineType(MachineType.Evolution),
                        parseMachineType(MachineType.Ew),
                        parseMachineType(MachineType.Ew2),
                        parseMachineType(MachineType.Ew2Pool),
                        parseMachineType(MachineType.Nova2Pool),
                        parseMachineType(MachineType.Nova2PoolP),
                        parseMachineType(MachineType.Unknown),
                      ]
                }
                initialVal={value}
                onChange={(v) => {
                  const newModules = [...modules];
                  const row = newModules[tableMeta.rowIndex];
                  const instrumentDisplay = v as string;
                  const nextModelVersion = resolveModelVersionAfterInstrumentChange(
                    row,
                    instrumentDisplay,
                  );
                  newModules[tableMeta.rowIndex] = {
                    ...row,
                    type: fromParsedToMachineType(instrumentDisplay),
                    modelVersion: nextModelVersion,
                  };
                  setFieldValue('modules', newModules);
                }}
              />
            </Box>
          );
        },
      },
    },
    // {
    //   name: 'tool',
    //   label: ls.tool.toUpperCase(),
    //   options: {
    //     // eslint-disable-next-line react/display-name
    //     customBodyRender: (value, tableMeta, updateValue) => {
    //       return (
    //         <Box>
    //           <SteielSelect
    //             allowedValues={["CYCL'EAU"]}
    //             initialVal={value}
    //             onChange={(v) => {
    //               const newModules = [...modules];
    //               newModules[tableMeta.rowIndex] = { ...newModules[tableMeta.rowIndex], tool: v };
    //               setFieldValue('modules', newModules);
    //             }}
    //           />
    //           {tableMeta.rowIndex === tableModules.length - 1 ? FixUiComp : null}
    //         </Box>
    //       );
    //     },
    //   },
    // },
    {
      name: 'version',
      label: ls.version.toUpperCase(),
      options: {
        // eslint-disable-next-line react/display-name
        customBodyRender: (value, tableMeta, updateValue) => {
          const allowedValues = getAllowedVersionLabelsForInstrument(
            tableModules[tableMeta.rowIndex].type,
          );
          return (
            <Box>
              <SteielSelect
                allowedValues={allowedValues}
                initialVal={value}
                onChange={(v) => {
                  const newModules = [...modules];
                  newModules[tableMeta.rowIndex] = {
                    ...newModules[tableMeta.rowIndex],
                    modelVersion: fromParsedToMachineVersion(v),
                  };
                  setFieldValue('modules', newModules);
                }}
              />
            </Box>
          );
        },
      },
    },
    {
      name: '',
      options: {
        // eslint-disable-next-line react/display-name
        customBodyRender: (value, tableMeta, updateValue) => {
          return (
            <Box>
              <IconButton
                onClick={() => {
                  const newModules = [...modules];
                  newModules.splice(tableMeta.rowIndex, 1);
                  setFieldValue('modules', newModules);
                }}
              >
                <ClearIcon color='primary' />
              </IconButton>
            </Box>
          );
        },
      },
    },
  ];

  return (
    <MUIDataTable
      title=''
      data={tableModules}
      columns={columns}
      options={{
        ...options,
        elevation: isMobile ? 0 : 1,
        rowHover: false,
        rowsPerPage: localStorage.getItem('modules_rowsPerPage')
          ? Number(localStorage.getItem('modules_rowsPerPage'))
          : 10,
        onChangeRowsPerPage: (numberOfRows) => {
          localStorage.setItem('modules_rowsPerPage', `${numberOfRows}`);
        },
      }}
    />
  );
};

export default ModulesTable;
